import React, { useState } from "react";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router-dom";
import { httpService } from "../../base/services/httpService.service";
import { useLocation } from "react-router-dom";
import Toast from "../../base/components/Toast";
import { LocalStorageService } from "../../base/services/local-storage.service";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { useTranslation } from "../../i18n/LanguageContext";
import SignupLayout from "./components/SignupLayout";
import ResendCodeButton from "./components/ResendCodeButton";

const initialValues = {
   code: ""
};

// Expert e-mail verification, reached after expert sign-up and from login when an
// expert's e-mail is still unconfirmed.
function ConfirmCode() {
   const { t } = useTranslation();
   const navigate = useNavigate();
   const location = useLocation();

   const [error, setError] = useState(false);
   const userID = LocalStorageService.getItem("userId");
   const searchParam = new URLSearchParams(location.search);
   const email = searchParam.get("email");
   const { executeRecaptcha } = useGoogleReCaptcha();

   const sentCode = async (values) => {
      if (!executeRecaptcha) {
         return;
      }

      const recaptchaToken = await executeRecaptcha("login");
      let formattedUserId = userID.replace(/"/g, "");
      try {
         await httpService
            .post(
               "/Accounts/confirm-email",
               {
                  userId: formattedUserId,
                  code: values.code.trim()
               },
               () => {},
               () => {},
               {
                  headers: {
                     "g-recaptcha-response": recaptchaToken
                  }
               }
            )
            .then(() => {
               navigate(`/auth/expert-registration-step-three`);
               Toast.success(t("code-verified-success-description"));
            });
      } catch (error) {
         const errorMessages =
            error.response &&
            error.response.data &&
            error.response.data.errors &&
            error.response.data.errors.map((error) => error.message);
         if (errorMessages && errorMessages.length > 0) {
            errorMessages.forEach((errorMessage) => {
               Toast.error(errorMessage);
               setError(true);
            });
         } else {
            setError(false);
         }
      }
   };

   const ConfirmCodeSchema = Yup.object().shape({
      code: Yup.string().required(t("verification-code-required-validation"))
   });

   return (
      <SignupLayout showcase="expert" backTo="/">
         <Formik
            initialValues={initialValues}
            validationSchema={ConfirmCodeSchema}
            onSubmit={(values) => sentCode(values)}
         >
            {({ handleChange, values, errors, isSubmitting }) => (
               <Form className="kap-org-verify">
                  <h1 className="kap-org-signup__title kap-org-verify__title">
                     {t("verification-code-title")}
                  </h1>
                  <p className="kap-org-verify__text">
                     {t("verification-code-sent-to-text")}
                     {email && <> [{email}]</>}
                  </p>

                  <input
                     className={`kap-org-verify__code${
                        errors.code || error ? " kap-org-verify__code--error" : ""
                     }`}
                     name="code"
                     maxLength={12}
                     onChange={(e) => {
                        handleChange(e);
                        // A new attempt clears the server's "incorrect code" message.
                        setError(false);
                     }}
                     value={values.code}
                     autoComplete="one-time-code"
                     aria-label={t("verification-code-title")}
                  />
                  {error && <div className="error-message mt-2">{t("code-incorrect-text")}</div>}
                  {errors.code && <div className="error-message mt-2">{errors.code}</div>}

                  <button
                     type="submit"
                     disabled={isSubmitting}
                     className="btn sign-btn font-weight-bold kap-org-verify__submit"
                  >
                     {t("verify-code-button")}
                  </button>

                  <p className="kap-org-verify__help">
                     {t("didnt-receive-code-text")}{" "}
                     <ResendCodeButton email={email} />{" "}
                     {t("or-send-email-at-text")}{" "}
                     <a href="mailto:info@kaptechgroup.com">info@kaptechgroup.com</a>
                  </p>
               </Form>
            )}
         </Formik>
      </SignupLayout>
   );
}

export default ConfirmCode;
